import React, { useRef, useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * PullToRefresh — Facebook-style pull-to-refresh for touch devices.
 *
 * Wraps the whole app. On a downward drag that STARTS while the window is
 * scrolled to the very top, it shows a spinner; releasing past the threshold
 * calls `onRefresh()` (an async function) and keeps the spinner until it
 * resolves.
 *
 * Cross-platform notes:
 *  - Uses raw touch events → works on every mobile browser + installed PWA.
 *  - Native browser pull-to-refresh is already disabled app-wide via
 *    `overscroll-behavior: none`, so there is no double-trigger.
 *  - Only the spinner element is transformed (a fixed element), never an
 *    ancestor of the page — so it never creates a containing block that would
 *    break the fixed bottom-nav / bottom-sheet positioning.
 *  - Desktop (mouse) never fires touch events, so it is inert there.
 *
 * Guards (won't trigger when):
 *  - a bottom sheet is open (`body.bottomsheet-open`)
 *  - the gesture starts inside a `[data-no-sheet-drag]` / `[data-no-pull-refresh]`
 *    area (charts, news reader, custom scrollers)
 *  - the page isn't at the top, or the gesture is horizontal / upward
 */
const THRESHOLD = 70;   // px of pull (after resistance) needed to trigger
const MAX_PULL = 110;   // visual cap
const RESISTANCE = 0.5; // drag dampening

export default function PullToRefresh({ onRefresh, children }) {
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);
  const contentRef = useRef(null); // the <main id="ptr-content"> element (cached)
  const [refreshing, setRefreshing] = useState(false);
  const drag = useRef({ startY: 0, startX: 0, pulling: false, decided: false, distance: 0 });

  // Lazily locate (and cache) the page content element we translate. It is a
  // sibling of the fixed header / bottom-nav, so transforming it pushes only the
  // tab content down — never the chrome — and never re-anchors fixed elements.
  const getContent = () => {
    if (!contentRef.current) contentRef.current = document.getElementById('ptr-content');
    return contentRef.current;
  };

  // Promote the moving layers to their own GPU compositor layer and kill any
  // transition — done ONCE when a drag begins so per-frame writes are cheap
  // (compositor-only, no layout/paint). This is the key to a smooth pull.
  const primeLayers = useCallback(() => {
    const el = indicatorRef.current;
    const c = getContent();
    if (el) { el.style.transition = 'none'; el.style.willChange = 'transform, opacity'; }
    if (c) { c.style.transition = 'none'; c.style.willChange = 'transform'; }
  }, []);

  // Write the current pull distance to the DOM. Uses translate3d so the browser
  // keeps the elements on the GPU layer (no repaint while dragging).
  const setVisual = useCallback((d) => {
    const eased = Math.min(d, MAX_PULL);
    const el = indicatorRef.current;
    if (el) {
      el.style.opacity = String(Math.min(1, d / THRESHOLD));
      el.style.transform = `translate3d(-50%, ${eased}px, 0) rotate(${d * 2.5}deg)`;
    }
    const c = contentRef.current;
    if (c) c.style.transform = `translate3d(0, ${eased}px, 0)`;
  }, []);

  const resetVisual = useCallback(() => {
    // Longer, gently-decelerating glide back (ease-out-expo-ish) so the release
    // feels smooth and unhurried rather than snapping shut.
    const ease = 'transform 0.55s cubic-bezier(0.16, 1, 0.3, 1)';
    const el = indicatorRef.current;
    if (el) {
      el.style.transition = `${ease}, opacity 0.45s ease`;
      el.style.opacity = '0';
      el.style.transform = 'translate3d(-50%, 0, 0)';
    }
    const c = contentRef.current;
    if (c) {
      c.style.transition = ease;
      c.style.transform = 'translate3d(0, 0, 0)';
    }
    // Once settled, drop transform + will-change so nothing lingers (a held
    // transform would create a containing block affecting fixed descendants).
    window.setTimeout(() => {
      if (indicatorRef.current) indicatorRef.current.style.willChange = '';
      if (contentRef.current) {
        contentRef.current.style.transition = '';
        contentRef.current.style.transform = '';
        contentRef.current.style.willChange = '';
      }
    }, 580);
  }, []);

  const triggerRefresh = useCallback(async () => {
    setRefreshing(true);
    const settle = 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
    const el = indicatorRef.current;
    if (el) {
      el.style.transition = `${settle}, opacity 0.3s ease`;
      el.style.opacity = '1';
      el.style.transform = `translate3d(-50%, ${THRESHOLD}px, 0)`;
    }
    const c = contentRef.current || getContent();
    if (c) {
      c.style.transition = settle;
      c.style.transform = `translate3d(0, ${THRESHOLD}px, 0)`;
    }
    const started = Date.now();
    try {
      await Promise.resolve(typeof onRefresh === 'function' ? onRefresh() : null);
    } catch (_e) { /* swallow — never break the UI */ }
    // Keep the spinner visible for a beat so it doesn't flicker.
    const elapsed = Date.now() - started;
    if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
    setRefreshing(false);
    resetVisual();
  }, [onRefresh, resetVisual]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const s = drag.current;
    let rafId = 0;

    // Batch DOM writes to one per animation frame — touchmove can fire faster
    // than the screen refreshes, so coalescing prevents stutter.
    const scheduleVisual = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (s.pulling) setVisual(s.distance);
      });
    };

    const isBlocked = (target) => {
      if (document.body.classList.contains('bottomsheet-open')) return true;
      if (target && target.closest && target.closest('[data-no-sheet-drag], [data-no-pull-refresh]')) return true;
      return false;
    };

    const onStart = (e) => {
      if (refreshing) return;
      if (window.scrollY > 0) return;
      if (isBlocked(e.target)) return;
      const t = e.touches[0];
      s.startY = t.clientY;
      s.startX = t.clientX;
      s.pulling = false;
      s.decided = false;
      s.distance = 0;
    };

    const onMove = (e) => {
      if (refreshing || s.startY === 0) return;
      const t = e.touches[0];
      const dy = t.clientY - s.startY;
      const dx = t.clientX - s.startX;

      if (!s.decided) {
        if (Math.abs(dy) < 6 && Math.abs(dx) < 6) return; // wait for intent
        s.decided = true;
        // Abort if horizontal, upward, not at top, or inside a blocked area.
        if (Math.abs(dx) > Math.abs(dy) || dy <= 0 || window.scrollY > 0 || isBlocked(e.target)) {
          s.pulling = false;
          return;
        }
        s.pulling = true;
        primeLayers(); // GPU-promote + freeze transitions before the first move
      }

      if (!s.pulling) return;
      if (window.scrollY > 0) { s.pulling = false; s.distance = 0; resetVisual(); return; }

      if (e.cancelable) e.preventDefault(); // stop the page from scrolling/rubber-banding
      // Progressive (elastic) damping — moves freely at first, then resists more
      // the further you pull, like an iOS rubber-band. Feels softer than linear.
      const raw = dy;
      s.distance = raw > 0 ? (raw * RESISTANCE) / (1 + raw / 600) : 0;
      scheduleVisual();
    };

    const onEnd = () => {
      if (refreshing) return;
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      const shouldRefresh = s.pulling && s.distance >= THRESHOLD;
      s.pulling = false;
      s.decided = false;
      s.startY = 0;
      if (shouldRefresh) {
        triggerRefresh();
      } else if (s.distance > 0) {
        s.distance = 0;
        resetVisual();
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [refreshing, setVisual, resetVisual, triggerRefresh, primeLayers]);

  return (
    <div ref={containerRef}>
      {/* Spinner indicator — fixed, starts hidden above the fold */}
      <div
        ref={indicatorRef}
        className="fixed left-1/2 z-[60] pointer-events-none"
        style={{ top: 6, opacity: 0, transform: 'translate3d(-50%, 0, 0)' }}
        aria-hidden="true"
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border-muted)',
            boxShadow: 'var(--shadow-soft)',
          }}
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} style={{ color: 'var(--color-accent)' }} />
        </div>
      </div>
      {children}
    </div>
  );
}
